import { useRef, useState } from 'react';
import { Box, Flex, Image, Text } from '@mantine/core';
import type { Client } from 'src/admin/users/types';
import CreateClientModal from 'src/clients/CreateClientModal';
import { ActionRow } from 'src/meetings/components/ActionRow';
import { useLinkNewClientToParticipant } from 'src/meetings/hooks/useLinkNewClientToParticipant';
import type { CalendarEvent, Participant } from 'src/meetings/models';
import { showSuccess } from 'src/toasts/toasts';
import { MeetingDetailsCard } from 'src/non-financial-data/v2/family/MeetingDetailsCard';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  event: CalendarEvent;
  // Called after a client is created and swapped in, so the parent can refresh
  // the event (and any transcript) to reflect the newly linked client.
  onChanged: () => Promise<void> | void;
  // When shown alongside the meeting-preparation cards, render inside a matching
  // bordered box so it reads as another card in that section.
  boxed?: boolean;
};

function avatarSeed(participant: Participant): string {
  return (participant.name || participant.emailAddress || '?').slice(0, 2);
}

function displayName(participant: Participant): string {
  return participant.name || participant.emailAddress || 'Unknown attendee';
}

function buildClientPrefill(participant: Participant): {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
} {
  const emailAddress = participant.emailAddress ?? undefined;
  const trimmedName = participant.name?.trim();
  if (!trimmedName) {
    return { emailAddress };
  }
  const [firstName, ...rest] = trimmedName.split(/\s+/);
  return { firstName, lastName: rest.length > 0 ? rest.join(' ') : undefined, emailAddress };
}

export function UnrecognisedAttendees({ event, onChanged, boxed = false }: Props) {
  const { mutateAsync: linkNewClient } = useLinkNewClientToParticipant(event.id ?? '');
  const [clientCandidate, setClientCandidate] = useState<Participant | null>(null);
  // The id of the attendee whose swap is in flight, so only that row shows a spinner
  // (all rows disable to prevent a second concurrent swap).
  const [linkingParticipantId, setLinkingParticipantId] = useState<string | null>(null);
  // The create-client modal clears clientCandidate on close, which fires before
  // onClientCreated; keep the participant id in a ref so the swap can still target it.
  const pendingParticipantIdRef = useRef<string | null>(null);

  const candidates = event.eventParticipants
    .map(eventParticipant => eventParticipant.participant)
    .filter(participant => participant.participantType === 'EXTERNAL' && !!participant.id);

  if (candidates.length === 0) {
    return null;
  }

  const handleCreateClient = (participant: Participant) => {
    pendingParticipantIdRef.current = participant.id;
    setClientCandidate(participant);
  };

  const handleClientCreated = async (client: Client) => {
    const participantId = pendingParticipantIdRef.current;
    if (!participantId) {
      return;
    }
    setLinkingParticipantId(participantId);
    try {
      await linkNewClient({ participantId, clientId: client.id });
      showSuccess('Client created and linked to meeting');
      await onChanged();
    } catch (error) {
      notifyAndLogError(
        'Client created but not linked',
        'The client was created, but we could not link them to this meeting. Use "Add existing client" to link them.',
        error,
      );
    } finally {
      setLinkingParticipantId(null);
      pendingParticipantIdRef.current = null;
    }
  };

  const description =
    "These people were in the meeting but aren't clients or advisers yet. Create a client to link them.";

  const body = (
    <>
      <Flex gap="sm" direction="column">
        {candidates.map(participant => {
          const subtitle =
            participant.name && participant.emailAddress ? participant.emailAddress : undefined;
          return (
            <ActionRow
              key={participant.id}
              icon={
                <Image
                  style={{ width: 40, height: 40 }}
                  radius="xl"
                  src={`https://ui-avatars.com/api/?background=0D8ABC&color=fff&name=${avatarSeed(participant)}`}
                />
              }
              title={displayName(participant)}
              subtitle={subtitle}
              button={{
                label: 'Create client',
                onClick: () => handleCreateClient(participant),
                variant: 'primary',
                loading: linkingParticipantId === participant.id,
                disabled: linkingParticipantId !== null,
              }}
            />
          );
        })}
      </Flex>

      {clientCandidate && (
        <CreateClientModal
          opened
          close={() => setClientCandidate(null)}
          prefill={buildClientPrefill(clientCandidate)}
          navigateAfterCreation={false}
          onClientCreated={handleClientCreated}
        />
      )}
    </>
  );

  // Alongside the meeting-preparation cards, render through MeetingDetailsCard so
  // the box, width and heading match the sibling cards exactly.
  if (boxed) {
    return (
      <MeetingDetailsCard title="Unrecognised attendees" description={description}>
        {body}
      </MeetingDetailsCard>
    );
  }

  return (
    <Box maw={800}>
      <Text fw={FONT_WEIGHTS.bold} mb="xxs">
        Unrecognised attendees
      </Text>
      <Text size="sm" c="gray.6" mb="sm">
        {description}
      </Text>
      {body}
    </Box>
  );
}
